// Initialize Sentry BEFORE wrapCreateBrowserRouterV7() is called
// https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/v7/#usage-with-createbrowserrouter-or-creatememoryrouter-data-mode
// This file is imported as a side-effect in routes.tsx

import * as Sentry from '@sentry/react'
import React from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router'

import { handleException } from 'clients/exception-handling'

const optionalTrackerErrorPatterns = [
  'LinkedIn Insight script failed to load',
  'Microsoft Clarity script failed to load',
  'Meta Pixel script failed to load',
  'https://snap.licdn.com/li.lms-analytics/insight.min.js',
  'https://connect.facebook.net/en_US/fbevents.js',
  'https://www.clarity.ms/tag/',
]

const optionalTrackerFramePatterns = [
  'https://client.crisp.chat/',
  'https://fast.chameleon.io/',
  'https://scripts.clarity.ms/',
  'https://snap.licdn.com/',
  'https://stonly.com/js/widget/',
]

const defaultTracePropagationTargets = ['localhost']

function getConfiguredApiTracePropagationTarget(): string | null {
  const apiBaseUrl = import.meta.env.VITE_YOGI_API_BASE_URL
  if (!apiBaseUrl) {
    return null
  }

  try {
    return new URL(apiBaseUrl).origin
  } catch (error) {
    handleException(error, {
      operation: 'telemetry.trace-target.parse',
      disposition: 'expected',
      isExpectedError: (cause) => cause instanceof TypeError,
      reason:
        'Invalid configured URL leaves trace propagation restricted to localhost.',
    })
    return null
  }
}

function getTracePropagationTargets(): (string | RegExp)[] {
  const apiTarget = getConfiguredApiTracePropagationTarget()
  if (!apiTarget || defaultTracePropagationTargets.includes(apiTarget)) {
    return defaultTracePropagationTargets
  }

  return [apiTarget, ...defaultTracePropagationTargets]
}

function isOptionalTrackerError(event: Sentry.Event): boolean {
  const messages = [
    event.message,
    event.exception?.values?.[0]?.value,
    event.exception?.values?.[0]?.mechanism?.data?.['message'],
  ].filter((message): message is string => typeof message === 'string')

  return messages.some((message) =>
    optionalTrackerErrorPatterns.some((pattern) => message?.includes(pattern)),
  )
}

function isOptionalTrackerFrame(event: Sentry.Event): boolean {
  const frames =
    event.exception?.values?.flatMap(
      (value) => value.stacktrace?.frames ?? [],
    ) ?? []

  return frames.some((frame) => {
    const location = frame.abs_path ?? frame.filename ?? ''
    return optionalTrackerFramePatterns.some((pattern) =>
      location.includes(pattern),
    )
  })
}

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.replayCanvasIntegration(),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracePropagationTargets: getTracePropagationTargets(),
    environment: import.meta.env.VITE_ENV,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: import.meta.env.VITE_ENV === 'prod' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      if (isOptionalTrackerError(event) || isOptionalTrackerFrame(event)) {
        return null
      }

      if (event.exception?.values?.length) {
        event.tags = {
          ...event.tags,
          feedback_type: 'bug',
          feedback_source:
            event.tags?.['error_source'] === 'handled'
              ? 'handled_error'
              : 'auto_crash',
        }
      }
      return event
    },
  })
}
